/**
 * The consultation's rules that are not components: how a category path is
 * named, which depot lines fall inside the session's scope, and which tabs
 * the fiche has.
 *
 * Apart from the screens so those files export only components, which is
 * what keeps fast refresh working on them; the tests read these from here.
 */

import type { AttributeDefinition, CategoryNode, DepotStock } from "./api";
import type { ConsultationScope, ScopeSelection } from "./scope";

export const ARTICLE_TABS = ["equivalents", "documents"] as const;
export type ArticleTab = (typeof ARTICLE_TABS)[number];

export interface PathLabel {
  label: string;
  /** Every label from the root down to this node, inclusive. */
  trail: string[];
  depth: number;
}

/** Each category path, with its label and the labels above it. */
export function labelsByPath(roots: readonly CategoryNode[]): Map<string, PathLabel> {
  const found = new Map<string, PathLabel>();
  const walk = (nodes: readonly CategoryNode[], above: string[]) => {
    for (const node of nodes) {
      const trail = [...above, node.category.label];
      found.set(node.category.path, {
        label: node.category.label,
        trail,
        depth: node.category.depth,
      });
      walk(node.children, trail);
    }
  };
  walk(roots, []);
  return found;
}

/** Whether a depot line falls inside the session's scope. */
export function inScope(line: DepotStock, scope: ConsultationScope): boolean {
  return (
    (!scope.companyCode || line.company_code === scope.companyCode) &&
    (!scope.city || line.city === scope.city) &&
    (!scope.pointOfSaleCode || line.point_of_sale_code === scope.pointOfSaleCode) &&
    (!scope.siteCode || line.site_code === scope.siteCode)
  );
}

/** Whether a depot line falls inside a selection: any chosen value of each level. */
export function inSelection(line: DepotStock, selection: ScopeSelection): boolean {
  return (
    (selection.companyCodes.length === 0 || selection.companyCodes.includes(line.company_code)) &&
    (selection.cities.length === 0 || selection.cities.includes(line.city)) &&
    (selection.pointOfSaleCodes.length === 0 ||
      selection.pointOfSaleCodes.includes(line.point_of_sale_code)) &&
    (selection.siteCodes.length === 0 || selection.siteCodes.includes(line.site_code))
  );
}

/**
 * A price with VAT added, for display: `"100.00"` at `"20"` % is `"120.00"`.
 *
 * Worked in cents so a price does not drift by a cent through binary
 * floating point, and rounded half away from zero as an invoice rounds. The
 * rate is the installation's — `orders` holds it — and arrives as a string,
 * the way the API carries every decimal.
 */
export function withTax(price: string, ratePct: string): string {
  const cents = Math.round(Number(price) * 100);
  const rate = Number(ratePct);
  if (!Number.isFinite(cents) || !Number.isFinite(rate)) return price;
  const taxed = Math.sign(cents) * Math.round((Math.abs(cents) * (100 + rate)) / 100);
  return (taxed / 100).toFixed(2);
}

/** Physical, reserved and available, summed — the three figures at every level. */
export interface StockTotals {
  real: number;
  reserved: number;
  available: number;
}

export interface StockPointOfSale {
  code: string;
  name: string;
  totals: StockTotals;
  depots: DepotStock[];
}

export interface StockLocation {
  /** The city; empty for depots nobody has placed. */
  city: string;
  totals: StockTotals;
  points: StockPointOfSale[];
}

function add(totals: StockTotals, line: DepotStock): void {
  totals.real += Number(line.real);
  totals.reserved += Number(line.reserved);
  totals.available += Number(line.available);
}

/**
 * The depot lines as the stock tree reads them: Localisation › Point de
 * vente › Dépôt, each level carrying its own totals.
 *
 * Sorted by name at every level, with the unplaced last: a depot with no
 * city or no point of sale is still stock, so it is shown — under a heading
 * that says so — rather than dropped from the totals.
 */
export function stockTree(lines: readonly DepotStock[]): StockLocation[] {
  const byCity = new Map<string, StockLocation>();
  for (const line of lines) {
    const city = byCity.get(line.city) ?? {
      city: line.city,
      totals: { real: 0, reserved: 0, available: 0 },
      points: [],
    };
    byCity.set(line.city, city);
    add(city.totals, line);
    let point = city.points.find((entry) => entry.code === line.point_of_sale_code);
    if (!point) {
      point = {
        code: line.point_of_sale_code,
        name: line.point_of_sale_name,
        totals: { real: 0, reserved: 0, available: 0 },
        depots: [],
      };
      city.points.push(point);
    }
    add(point.totals, line);
    point.depots.push(line);
  }
  const last = (empty: boolean) => (empty ? 1 : 0);
  const locations = [...byCity.values()].sort(
    (a, b) => last(!a.city) - last(!b.city) || a.city.localeCompare(b.city),
  );
  for (const location of locations) {
    location.points.sort((a, b) => last(!a.code) - last(!b.code) || a.name.localeCompare(b.name));
    for (const point of location.points) {
      point.depots.sort((a, b) => a.site_name.localeCompare(b.site_name));
    }
  }
  return locations;
}

export interface TechnicalRow {
  key: string;
  /** The group's label, unit included; the bare key for a value outside it. */
  label: string;
  value: string;
  /** Declared by the article's attribute group, as opposed to carried anyway. */
  declared: boolean;
}

/**
 * The technical characteristics as a desk reads them: the attribute group's
 * own fields first, in the group's order and under the group's labels —
 * `Largeur (mm)`, not `tyre.width` — empty ones included, since "not filled
 * in" is an answer; then whatever else the article carries, under its key.
 */
export function technicalRows(
  definitions: readonly AttributeDefinition[],
  values: readonly { key: string; value: string }[],
): TechnicalRow[] {
  const byKey = new Map(values.map((item) => [item.key, item.value]));
  const declared = [...definitions]
    .sort((a, b) => a.position - b.position)
    .map((definition) => ({
      key: definition.key,
      label: definition.unit ? `${definition.label} (${definition.unit})` : definition.label,
      value: byKey.get(definition.key) ?? "",
      declared: true,
    }));
  const known = new Set(definitions.map((definition) => definition.key));
  const others = values
    .filter((item) => !known.has(item.key))
    .map((item) => ({ key: item.key, label: item.key, value: item.value, declared: false }));
  return [...declared, ...others];
}
